import { supabase } from './supabase';

// Generate a unique ID
export function generateId() {
  return `audit_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
}

// Get all audits from Supabase
export async function getAudits() {
  // Return localStorage immediately, then try Supabase in background
  const local = getAuditsLocal();

  try {
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), 5000); // 5 second timeout

    const { data, error } = await supabase
      .from('audits')
      .select('id, intake, scores, created_at, updated_at, status')
      .order('created_at', { ascending: false })
      .abortSignal(controller.signal);

    clearTimeout(timeout);
    if (error) throw error;
    const dbAudits = (data || []).map(mapFromDB);

    // Merge: DB is source of truth, but keep local-only audits and local-only fields
    const localMap = {};
    local.forEach(a => { localMap[a.id] = a; });
    const merged = dbAudits.map(a => ({
      ...a,
      status: localMap[a.id]?.status || a.status || 'draft',
      branding: localMap[a.id]?.branding || a.branding || null,
    }));
    const dbIds = new Set(dbAudits.map(a => a.id));
    const localOnly = local.filter(a => !dbIds.has(a.id));
    return [...merged, ...localOnly];
  } catch (err) {
    console.warn('Supabase fetch failed, using local:', err.message);
    return local;
  }
}

// Get a single audit by ID
export async function getAudit(id) {
  // Try local first for instant load
  const local = getAuditLocal(id);

  try {
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), 5000);

    const { data, error } = await supabase
      .from('audits')
      .select('*')
      .eq('id', id)
      .limit(1)
      .abortSignal(controller.signal);

    const record = Array.isArray(data) ? data[0] : data;
    if (!record) throw new Error('Not found');

    clearTimeout(timeout);
    if (error) throw error;
    const fromDB = mapFromDB(record);
    // Local storage is authoritative for fields that don't have DB columns
    if (local) {
      fromDB.status = local.status || fromDB.status || 'draft';
      fromDB.branding = local.branding || fromDB.branding || null;
      fromDB.journeyTeam = local.journeyTeam || fromDB.journeyTeam || null;
      fromDB.trainingModules = (local.trainingModules?.length > 0 ? local.trainingModules : fromDB.trainingModules) || [];
    }
    // Also save the merged result back to localStorage so it stays current
    saveAuditLocal(fromDB);
    return fromDB;
  } catch (err) {
    console.warn('Supabase audit fetch failed, using local:', err.message);
    return local;
  }
}

// Save an audit to Supabase with edit tracking
export async function saveAudit(audit, actionLabel) {
  try {
    // Get current user for edit tracking
    const { data: { user } } = await supabase.auth.getUser();
    const editorName = user?.user_metadata?.full_name || user?.email || 'Unknown';
    const editorId = user?.id || null;

    // Add to edit log — throttle to avoid flooding (max 1 entry per 5 seconds per user)
    const editLog = audit.editLog || [];
    const lastEntry = editLog[editLog.length - 1];
    const now = new Date();
    const lastTime = lastEntry ? new Date(lastEntry.timestamp) : null;
    const shouldLog = !lastTime || (now - lastTime) > 5000 || lastEntry.userId !== editorId;
    if (shouldLog) {
      editLog.push({
        user: editorName,
        userId: editorId,
        timestamp: now.toISOString(),
        action: actionLabel || (editLog.length === 0 ? 'Created audit' : 'Updated audit'),
      });
      // Keep log manageable — max 200 entries
      if (editLog.length > 200) editLog.splice(0, editLog.length - 200);
    }
    audit.editLog = editLog;
    audit.lastEditedBy = editorName;
    audit.lastEditedAt = new Date().toISOString();

    const dbRecord = mapToDB(audit);
    // Remove fields that may not exist as DB columns to prevent upsert failure
    delete dbRecord.status;
    delete dbRecord.branding;
    delete dbRecord.training_modules;
    delete dbRecord.journey_team;
    const { data, error } = await supabase
      .from('audits')
      .upsert(dbRecord, { onConflict: 'id' })
      .select()
      .single();
    if (error) {
      console.error('Supabase upsert error:', error);
      throw error;
    }
    saveAuditLocal(audit);
    return mapFromDB(data);
  } catch (err) {
    console.error('Error saving audit:', err);
    saveAuditLocal(audit);
    return audit;
  }
}

// Delete an audit
export async function deleteAudit(id) {
  try {
    const { error } = await supabase.from('audits').delete().eq('id', id);
    if (error) throw error;
    deleteAuditLocal(id);
  } catch (err) {
    console.error('Error deleting audit:', err);
    deleteAuditLocal(id);
  }
}

// Map app format to DB columns
function mapToDB(audit) {
  return {
    id: audit.id,
    intake: audit.intake || {},
    scores: audit.scores || {},
    ai_research: audit.aiResearch || null,
    revenue_leaks: audit.revenueLeaks || null,
    memory_test: audit.memoryTest || null,
    social_proof: audit.socialProof || null,
    brand_coherence: audit.brandCoherence || null,
    branding: audit.branding || null,
    approved_competitors: audit.approvedCompetitors || null,
    market_data: audit.marketData || null,
    comments: audit.comments || [],
    photos: audit.photos || [],
    menu_analysis: audit.menuAnalysis || null,
    beverage_analysis: audit.beverageAnalysis || null,
    report_sections: audit.reportSections || null,
    roadmap_progress: audit.roadmapProgress || null,
    roadmap_comments: audit.roadmapComments || [],
    team_scores: audit.teamScores || null,
    menu_items: audit.menuItems || null,
    custom_weights: audit.customWeights || null,
    experience_map: audit.experienceMap || null,
    revenue_labor: audit.revenueLabor || null,
    digital_friction: audit.digitalFriction || null,
    guest_journey: {
      _active: audit.guestJourney || null,
      _visits: audit.guestJourneys || null,
      _activeIndex: audit._activeVisitIndex || 0,
    },
    content_calendar: {
      _items: audit.contentCalendar || null,
      _startDate: audit.calendarStartDate || null,
    },
    signature_moments: audit.signatureMoments || null,
    training_modules: audit.trainingModules || null,
    edit_log: audit.editLog || [],
    last_edited_by: audit.lastEditedBy || null,
    created_by: audit.createdBy || null,
    created_by_name: audit.createdByName || null,
    created_at: audit.createdAt || new Date().toISOString(),
    updated_at: new Date().toISOString(),
    status: audit.status || 'draft',
    // Pack new fields into report_sections JSON to avoid missing DB columns
    report_sections: {
      ...(audit.reportSections || {}),
      _meta: {
        status: audit.status || 'draft',
        branding: audit.branding || null,
        journeyTeam: audit.journeyTeam || null,
        calendarStartDate: audit.calendarStartDate || null,
      },
    },
  };
}

// Map DB columns to app format
// Ensure value is an array
function ensureArray(val) {
  if (Array.isArray(val)) return val;
  if (val === null || val === undefined) return [];
  return [];
}

function mapFromDB(record) {
  if (!record) return null;
  return {
    id: record.id,
    intake: (typeof record.intake === 'object' && record.intake) ? record.intake : {},
    scores: (typeof record.scores === 'object' && record.scores) ? record.scores : {},
    aiResearch: record.ai_research ? {
      ...record.ai_research,
      scoreTensions: ensureArray(record.ai_research.scoreTensions),
      revenueLeaks: ensureArray(record.ai_research.revenueLeaks),
      competitorAnalysis: ensureArray(record.ai_research.competitorAnalysis),
    } : null,
    revenueLeaks: (typeof record.revenue_leaks === 'object' && record.revenue_leaks) ? record.revenue_leaks : null,
    memoryTest: record.memory_test || null,
    socialProof: record.social_proof || null,
    brandCoherence: record.brand_coherence || null,
    branding: record.branding || null,
    approvedCompetitors: ensureArray(record.approved_competitors),
    marketData: record.market_data ? {
      ...record.market_data,
      competitors: ensureArray(record.market_data.competitors),
    } : null,
    comments: ensureArray(record.comments),
    photos: ensureArray(record.photos),
    menuAnalysis: record.menu_analysis || null,
    beverageAnalysis: record.beverage_analysis || null,
    reportSections: record.report_sections ? (() => {
      const rs = { ...record.report_sections };
      delete rs._meta; // Don't pollute reportSections with meta
      return Object.keys(rs).length > 0 ? rs : null;
    })() : null,
    roadmapProgress: record.roadmap_progress || null,
    roadmapComments: ensureArray(record.roadmap_comments),
    teamScores: record.team_scores || null,
    menuItems: ensureArray(record.menu_items),
    customWeights: record.custom_weights || null,
    experienceMap: record.experience_map || null,
    revenueLabor: record.revenue_labor || null,
    digitalFriction: record.digital_friction || null,
    guestJourney: record.guest_journey?._active || (record.guest_journey && !record.guest_journey._visits ? record.guest_journey : null),
    guestJourneys: record.guest_journey?._visits || null,
    _activeVisitIndex: record.guest_journey?._activeIndex || 0,
    contentCalendar: ensureArray(record.content_calendar?._items || (Array.isArray(record.content_calendar) ? record.content_calendar : null)),
    calendarStartDate: record.content_calendar?._startDate || null,
    signatureMoments: ensureArray(record.signature_moments),
    trainingModules: ensureArray(record.training_modules),
    editLog: ensureArray(record.edit_log),
    lastEditedBy: record.last_edited_by || null,
    createdBy: record.created_by || null,
    createdByName: record.created_by_name || null,
    createdAt: record.created_at,
    updatedAt: record.updated_at,
    status: record.status || record.report_sections?._meta?.status || 'draft',
    branding: record.branding || record.report_sections?._meta?.branding || null,
    journeyTeam: record.journey_team || record.report_sections?._meta?.journeyTeam || null,
  };
}

// ==========================================
// localStorage fallback (backup)
// ==========================================
const STORAGE_KEY = 'whats_next_audits';

// Sanitize any audit object to ensure all array fields are arrays
function sanitizeAudit(a) {
  if (!a) return null;
  return {
    ...a,
    scores: (typeof a.scores === 'object' && a.scores && !Array.isArray(a.scores)) ? a.scores : {},
    intake: (typeof a.intake === 'object' && a.intake && !Array.isArray(a.intake)) ? a.intake : {},
    approvedCompetitors: ensureArray(a.approvedCompetitors),
    comments: ensureArray(a.comments),
    photos: ensureArray(a.photos),
    menuItems: ensureArray(a.menuItems),
    contentCalendar: ensureArray(a.contentCalendar),
    signatureMoments: ensureArray(a.signatureMoments),
    editLog: ensureArray(a.editLog),
    roadmapComments: ensureArray(a.roadmapComments),
    marketData: a.marketData ? {
      ...a.marketData,
      competitors: ensureArray(a.marketData?.competitors),
    } : null,
    aiResearch: a.aiResearch ? {
      ...a.aiResearch,
      scoreTensions: ensureArray(a.aiResearch?.scoreTensions),
      revenueLeaks: ensureArray(a.aiResearch?.revenueLeaks),
      competitorAnalysis: ensureArray(a.aiResearch?.competitorAnalysis),
    } : null,
    guestJourney: a.guestJourney || null,
    guestJourneys: Array.isArray(a.guestJourneys) ? a.guestJourneys : null,
    _activeVisitIndex: a._activeVisitIndex || 0,
    calendarStartDate: a.calendarStartDate || null,
  };
}

function getAuditsLocal() {
  try {
    const data = localStorage.getItem(STORAGE_KEY);
    return data ? JSON.parse(data).map(sanitizeAudit).filter(Boolean) : [];
  } catch { return []; }
}

function getAuditLocal(id) {
  const found = getAuditsLocal().find(a => a.id === id);
  return found ? sanitizeAudit(found) : null;
}

function saveAuditLocal(audit) {
  const audits = getAuditsLocal();
  const idx = audits.findIndex(a => a.id === audit.id);
  if (idx >= 0) {
    audits[idx] = { ...audits[idx], ...audit, updatedAt: new Date().toISOString() };
  } else {
    audits.push({ ...audit, createdAt: audit.createdAt || new Date().toISOString(), updatedAt: new Date().toISOString() });
  }
  localStorage.setItem(STORAGE_KEY, JSON.stringify(audits));
}

function deleteAuditLocal(id) {
  const audits = getAuditsLocal().filter(a => a.id !== id);
  localStorage.setItem(STORAGE_KEY, JSON.stringify(audits));
}
